import Request from "./index";
import { completeDiameterRows } from "../utils/pricing-diameters";
import { CustomerPricing, PriceList, ShapeException } from "../utils/supplier-pricing-engine";

/* ------------------------------------------------------------------ */
/* API <-> client mapping (API is snake_case, UI state is camelCase)   */
/* ------------------------------------------------------------------ */

const n = (v: any): number | null => (v === null || v === undefined || v === "" ? null : Number(v));

export const priceListFromApi = (d: any): PriceList => ({
  vatPercent: Number(d.vat_percent),
  wastePercent: Number(d.waste_percent),
  quoteValidityDays: Number(d.quote_validity_days),
  cutPerTon: n(d.cut_per_ton),
  bendPerTon: n(d.bend_per_ton),
  unitPrice: n(d.unit_price),
  minUnitPrice: n(d.min_unit_price),
  shortShapeAsUnit: Boolean(d.short_shape_as_unit),
  shortShapeMaxCm: n(d.short_shape_max_cm),
  transportMethod: d.transport_method,
  transportPrice: n(d.transport_price),
  transportFreeAboveTons: n(d.transport_free_above_tons),
  transportMinimum: n(d.transport_minimum),
  diameterPrices: completeDiameterRows((d.diameter_prices || []).map((r: any) => ({
    diameter: Number(r.diameter),
    ribbed: n(r.ribbed),
    smooth: n(r.smooth),
    weldableExtra: n(r.weldable_extra),
  })), diameter => ({ diameter, ribbed: null, smooth: null, weldableExtra: null })),
  shapeExceptions: (d.shape_exceptions || []).map(shapeExceptionFromApi),
  lastModified: d.last_modified,
  updatedByName: d.updated_by_name,
});

export const priceListToApi = (p: PriceList) => ({
  vat_percent: p.vatPercent,
  waste_percent: p.wastePercent,
  quote_validity_days: p.quoteValidityDays,
  cut_per_ton: p.cutPerTon,
  bend_per_ton: p.bendPerTon,
  unit_price: p.unitPrice,
  min_unit_price: p.minUnitPrice,
  short_shape_as_unit: p.shortShapeAsUnit,
  short_shape_max_cm: p.shortShapeMaxCm,
  transport_method: p.transportMethod,
  transport_price: p.transportPrice,
  transport_free_above_tons: p.transportFreeAboveTons,
  transport_minimum: p.transportMinimum,
  diameter_prices: p.diameterPrices.map((r) => ({
    diameter: r.diameter,
    ribbed: r.ribbed,
    smooth: r.smooth,
    weldable_extra: r.weldableExtra,
  })),
});

export const shapeExceptionFromApi = (e: any): ShapeException => ({
  id: e.id,
  shape: e.shape,
  shapeName: e.shape_name,
  shapeNumber: e.shape_number,
  chargeType: e.charge_type,
  processingPerTon: n(e.processing_per_ton),
  unitPrice: n(e.unit_price),
  customers: e.customers || [],
});

const shapeExceptionToApi = (e: ShapeException) => ({
  shape: e.shape,
  charge_type: e.chargeType,
  processing_per_ton: e.processingPerTon,
  unit_price: e.unitPrice,
  customers: e.customers,
});

export const customerPricingFromApi = (d: any): CustomerPricing => ({
  customer: d.customer,
  customerName: d.customer_name,
  mode: d.mode,
  cutPerTon: n(d.cut_per_ton),
  bendPerTon: n(d.bend_per_ton),
  unitPrice: n(d.unit_price),
  discountPercent: Number(d.discount_percent || 0),
  wastePercent: n(d.waste_percent),
  paymentTerms: d.payment_terms,
  transportMode: d.transport_mode,
  transportPrice: n(d.transport_price),
  transportFreeAboveTons: n(d.transport_free_above_tons),
  diameterOverrides: (d.diameter_overrides || []).map((o: any) => ({ diameter: Number(o.diameter), pricePerTon: n(o.price_per_ton) })),
  lastModified: d.last_modified,
});

const customerPricingToApi = (c: CustomerPricing) => ({
  mode: c.mode,
  cut_per_ton: c.cutPerTon ?? null,
  bend_per_ton: c.bendPerTon ?? null,
  unit_price: c.unitPrice ?? null,
  discount_percent: c.discountPercent,
  waste_percent: c.wastePercent,
  payment_terms: c.paymentTerms,
  transport_mode: c.transportMode,
  transport_price: c.transportPrice,
  transport_free_above_tons: c.transportFreeAboveTons,
  diameter_overrides: c.diameterOverrides.map((o) => ({ diameter: o.diameter, price_per_ton: o.pricePerTon })),
});

/* ------------------------------------------------------------------ */
/* endpoints                                                            */
/* ------------------------------------------------------------------ */

export const getSupplierPricing = () => Request.get("supplier/pricing/");
export const putSupplierPricing = (p: PriceList) => Request.put("supplier/pricing/", priceListToApi(p));

export const getPricingHistory = (limit = 50) => Request.get(`supplier/pricing/history/?limit=${limit}`);
export const restorePricingHistory = (id: number) => Request.post(`supplier/pricing/history/${id}/restore/`, {});

export const createShapeException = (e: ShapeException) => Request.post("supplier/pricing/shape-exceptions/", shapeExceptionToApi(e));
export const updateShapeException = (id: number, e: ShapeException) =>
  Request.patch(`supplier/pricing/shape-exceptions/${id}/`, shapeExceptionToApi(e));
export const deleteShapeException = (id: number) => Request.remove(`supplier/pricing/shape-exceptions/${id}/`);

export const getCustomPricedCustomers = () => Request.get("supplier/pricing/customers/");
export const getCustomerPricing = (customerId: number) => Request.get(`supplier/customers/${customerId}/pricing/`);
export const putCustomerPricing = (customerId: number, c: CustomerPricing) =>
  Request.put(`supplier/customers/${customerId}/pricing/`, customerPricingToApi(c));

export const getOrderPricing = (orderId: number) => Request.get(`supplier/orders/${orderId}/pricing/`);
export const setOrderPriceList = (orderId: number, priceList: string) =>
  Request.patch(`supplier/orders/${orderId}/pricing/`, { price_list: priceList, selection_only: true });
export const createOrderQuote = (orderId: number, priceList: string) =>
  Request.post(`supplier/orders/${orderId}/quote/`, { price_list: priceList, regenerate: true });
export const sendOrderQuote = (orderId: number, data: { channel: "email" | "whatsapp"; to: string; message: string; allow_approval: boolean }) =>
  Request.post(`supplier/orders/${orderId}/quote/send/`, data);
